import { Sparkles, Brain, ShieldAlert, Lock, FileWarning, Mail, BookOpen } from "lucide-react";
import SEO from "@/components/SEO";

const SUPPORT_EMAIL = "support@scudononni.it";

export default function TrasparenzaAI() {
  return (
    <div data-testid="trasparenza-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <SEO
        path="/trasparenza-ai"
        title="Trasparenza sull'Intelligenza Artificiale"
        description="Informativa di trasparenza sull'uso dell'IA in ScudoNonni: cosa fa, quali sono i limiti, come tuteliamo i tuoi diritti ai sensi dell'EU AI Act."
      />

      <div className="text-center mb-12 animate-fade-up">
        <Brain size={56} className="mx-auto text-[#1E3A8A] mb-5" />
        <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1E3A8A] leading-tight">
          Trasparenza sull'Intelligenza Artificiale
        </h1>
        <p className="mt-4 text-lg text-gray-600">
          Informativa ai sensi del Regolamento UE 2024/1689 ("EU AI Act") e del GDPR
        </p>
        <p className="mt-2 text-base text-gray-500">Ultimo aggiornamento: 27 febbraio 2026</p>
      </div>

      <div className="bg-white rounded-[2rem] shadow-lg border-4 border-blue-50 p-6 md:p-12 space-y-8 text-lg md:text-xl text-gray-700 leading-relaxed">

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3 flex items-center gap-3">
            <Sparkles size={28} className="text-[#1E3A8A]" /> Stai interagendo con un'IA
          </h2>
          <p>
            Quando usi lo strumento di verifica messaggi, <strong>il testo che scrivi o l'immagine che carichi
            vengono analizzati da un sistema di intelligenza artificiale</strong>. Questo lo dichiariamo
            apertamente, come previsto dall'art. 50 dell'EU AI Act, applicabile dal 2 agosto 2026.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3 flex items-center gap-3">
            <Brain size={28} className="text-[#22C55E]" /> Quale sistema di IA usiamo
          </h2>
          <ul className="list-disc pl-8 space-y-2">
            <li><strong>Modello</strong>: Google Gemini 3 Flash (modello multimodale di Google LLC)</li>
            <li><strong>Tipo</strong>: modello linguistico/visivo di uso generale (general-purpose AI model)</li>
            <li><strong>Categoria di rischio EU AI Act</strong>: limitata (limited risk) — soggetto a obblighi di trasparenza, non ad alto rischio</li>
            <li><strong>Fornitore</strong>: Google Ireland Limited (sede UE) / Google LLC</li>
            <li><strong>Fornitore intermedio dell'accesso</strong>: Emergent — usiamo la loro chiave universale</li>
            <li><strong>Finalità d'uso nel nostro contesto</strong>: classificare un messaggio come "sicuro", "sospetto" o "truffa" e fornire una breve spiegazione</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3 flex items-center gap-3">
            <ShieldAlert size={28} className="text-[#D97706]" /> Limiti dell'IA (cosa NON è)
          </h2>
          <p className="mb-3">
            L'IA è uno strumento utile, ma <strong>può sbagliare</strong>. Devi sempre tenere a mente:
          </p>
          <ul className="list-disc pl-8 space-y-2">
            <li>Il risultato è un <strong>orientamento</strong>, non una garanzia di sicurezza</li>
            <li>L'IA può sbagliare nel riconoscere una truffa o, al contrario, nel classificare come sospetto un messaggio innocuo</li>
            <li>L'IA <strong>non ti rappresenta legalmente</strong> e non sostituisce il parere di un avvocato, un consulente bancario o delle forze dell'ordine</li>
            <li>L'IA <strong>non ha memoria fra una verifica e l'altra</strong>: ogni analisi è indipendente</li>
            <li>L'IA può essere <strong>aggirata da truffatori esperti</strong> che usano nuove tecniche non ancora viste</li>
          </ul>
          <div className="mt-4 bg-[#FEF3C7] border-2 border-[#D97706] rounded-2xl p-5">
            <p className="text-[#78350F] font-semibold">
              Nel dubbio: <strong>non agire da solo</strong>. Chiama un familiare di fiducia, la tua banca al numero
              ufficiale, o la Polizia Postale al 06 4654 1.
            </p>
          </div>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3 flex items-center gap-3">
            <Lock size={28} className="text-[#1E3A8A]" /> Quali dati invii all'IA
          </h2>
          <p className="mb-3">
            Applichiamo il principio di <strong>minimizzazione dei dati</strong> (art. 5.1.c GDPR):
          </p>
          <ul className="list-disc pl-8 space-y-2">
            <li><strong>SOLO</strong> il testo o l'immagine che tu scegli di inviare</li>
            <li><strong>NESSUN</strong> dato personale aggiuntivo (niente nome, email, posizione, dispositivo, cookie)</li>
            <li>Il testo è incapsulato fra marcatori speciali per evitare che eventuali istruzioni nascoste possano alterare il sistema (protezione "prompt injection")</li>
            <li>Le immagini sono validate (solo JPG/PNG/WEBP, max 8 MB) per impedire il caricamento di file pericolosi</li>
          </ul>
          <p className="mt-3">
            <strong>Non memorizziamo</strong> il testo né l'immagine che invii: una volta ricevuta la risposta
            dell'IA, i contenuti vengono dimenticati. Salviamo solo metadati anonimi (data, livello di rischio
            restituito, tipo di input) per generare statistiche aggregate sulle truffe più diffuse.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3 flex items-center gap-3">
            <FileWarning size={28} className="text-[#1E3A8A]" /> Cosa NON facciamo con l'IA
          </h2>
          <ul className="list-disc pl-8 space-y-2">
            <li><strong>Non</strong> ti profiliamo né usiamo l'IA per prendere decisioni che hanno effetti giuridici su di te</li>
            <li><strong>Non</strong> usiamo l'IA per riconoscimento biometrico, emozionale o categorizzazione di persone</li>
            <li><strong>Non</strong> usiamo l'IA per pubblicità mirata o vendita di dati</li>
            <li><strong>Non</strong> creiamo profili psicologici o di vulnerabilità degli utenti anziani</li>
            <li>Le pratiche vietate dall'art. 5 EU AI Act <strong>non sono presenti</strong> in ScudoNonni</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3 flex items-center gap-3">
            <BookOpen size={28} className="text-[#22C55E]" /> I tuoi diritti
          </h2>
          <p className="mb-3">Ai sensi di GDPR ed EU AI Act, hai sempre il diritto di:</p>
          <ul className="list-disc pl-8 space-y-2">
            <li><strong>Sapere</strong> che stai interagendo con un'IA (questa pagina lo dichiara)</li>
            <li><strong>Ricevere una spiegazione</strong> del risultato (il sistema ti dà sempre una motivazione chiara)</li>
            <li><strong>Chiedere una revisione umana</strong> della verifica scrivendoci a <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#1E3A8A] font-bold underline">{SUPPORT_EMAIL}</a></li>
            <li><strong>Non usare</strong> lo strumento se non ti convince — puoi sempre rivolgerti alla Polizia Postale</li>
            <li><strong>Segnalare</strong> un errore dell'IA (falso positivo / falso negativo) via email — ci aiuti a migliorare il servizio</li>
            <li><strong>Reclamare</strong> presso il Garante per la protezione dei dati personali (www.garanteprivacy.it) o l'AI Office UE</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">
            Misure di sicurezza adottate
          </h2>
          <ul className="list-disc pl-8 space-y-2">
            <li>Comunicazione HTTPS cifrata fra il tuo dispositivo, i nostri server e l'IA</li>
            <li>Protezione contro <em>prompt injection</em> (tentativi di manipolare l'IA con istruzioni nascoste nel messaggio)</li>
            <li>Limite di 3 verifiche al giorno per dispositivo <strong>e</strong> per indirizzo IP (impedisce abusi e contiene i costi del servizio gratuito)</li>
            <li>Limite di velocità delle richieste (rate limiting) per fermare bot e attacchi automatici</li>
            <li>Validazione dei file caricati (magic-byte detection): rifiutiamo file mascherati da immagini</li>
            <li>IP anonimizzato tramite hash SHA-256 con salt prima di salvarlo</li>
            <li>Conservazione dei log tecnici limitata a 30 giorni</li>
          </ul>
        </section>

        <section className="bg-[#DBEAFE] rounded-2xl p-6">
          <h2 className="font-heading text-2xl font-bold text-[#1E3A8A] mb-3 flex items-center gap-3">
            <Mail size={28} /> Hai domande sull'IA?
          </h2>
          <p>
            Scrivici a <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#1E3A8A] font-bold underline">{SUPPORT_EMAIL}</a>.
            Ti rispondiamo entro 30 giorni come previsto dal GDPR (di solito molto prima).
          </p>
        </section>

      </div>
    </div>
  );
}
